import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Search, Target, TrendingUp, BarChart3, Globe, Zap, 
  ChevronRight, Play, BookOpen, CheckCircle, ArrowRight,
  Brain, Sparkles, Bot, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';

const SEOGuidePage = () => {
  const [activeTab, setActiveTab] = useState('seo');

  // Article Schema JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Complete Guide to SEO, AEO & GEO Automation in 2025",
    "description": "Learn how AutomateSEO.ai automates your entire SEO, AEO (Answer Engine Optimization), and GEO (Generative Engine Optimization) strategy with AI.",
    "image": "https://automateseo.ai/seo-guide-hero.jpg",
    "author": {
      "@type": "Organization",
      "name": "AutomateSEO.ai",
      "url": "https://automateseo.ai"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AutomateSEO.ai",
      "logo": {
        "@type": "ImageObject",
        "url": "https://automateseo.ai/logo.png"
      }
    },
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://automateseo.ai/seo-guide"
    }
  };

  // FAQ Schema for the guide
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is SEO Automation?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SEO Automation uses AI and machine learning to automatically handle repetitive SEO tasks like keyword research, content optimization, technical audits, and link building. AutomateSEO.ai automates 90% of SEO work, saving businesses 40+ hours per month."
        }
      },
      {
        "@type": "Question",
        "name": "What is AEO (Answer Engine Optimization)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AEO (Answer Engine Optimization) optimizes your content to appear in AI-powered answer engines like ChatGPT, Google's AI Overview, and voice assistants. It focuses on structured data, FAQ schemas, and direct answer formatting."
        }
      },
      {
        "@type": "Question",
        "name": "What is GEO (Generative Engine Optimization)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "GEO (Generative Engine Optimization) is the next evolution of SEO, focused on optimizing content for AI models that generate responses. It ensures your brand and content are cited by AI systems like ChatGPT, Perplexity, and Google Gemini."
        }
      },
      {
        "@type": "Question",
        "name": "How does AutomateSEO.ai work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AutomateSEO.ai uses AI agents to automatically analyze your website, generate optimized content, fix technical SEO issues, manage social media, and track rankings. Simply connect your website and let our AI handle everything."
        }
      }
    ]
  };

  // HowTo Schema
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Automate Your SEO with AI",
    "description": "Step-by-step guide to setting up automated SEO for your business using AutomateSEO.ai",
    "totalTime": "PT10M",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Sign Up",
        "text": "Create your free AutomateSEO.ai account in under 2 minutes",
        "url": "https://automateseo.ai/register"
      },
      {
        "@type": "HowToStep",
        "name": "Connect Your Website",
        "text": "Add your website URL and connect Google Search Console for data access"
      },
      {
        "@type": "HowToStep",
        "name": "Configure AI Agents",
        "text": "Set your target keywords, content preferences, and automation frequency"
      },
      {
        "@type": "HowToStep",
        "name": "Launch Automation",
        "text": "Activate the AI and watch as it optimizes your SEO automatically"
      }
    ]
  };

  const tabs = [
    { id: 'seo', label: 'SEO Basics', icon: Search },
    { id: 'aeo', label: 'AEO Guide', icon: Brain },
    { id: 'geo', label: 'GEO Strategy', icon: Sparkles },
    { id: 'automation', label: 'AI Automation', icon: Bot }
  ];

  const seoContent = {
    seo: {
      title: "SEO (Search Engine Optimization)",
      subtitle: "Master the fundamentals of ranking higher on Google",
      sections: [
        {
          title: "On-Page SEO",
          icon: FileText,
          items: [
            "Title tag optimization (60 characters max)",
            "Meta description crafting (160 characters)",
            "Header hierarchy (H1-H6 structure)",
            "Keyword density optimization",
            "Internal linking strategy",
            "Image alt text optimization"
          ]
        },
        {
          title: "Technical SEO",
          icon: Zap,
          items: [
            "Page speed optimization (Core Web Vitals)",
            "Mobile-first indexing compliance",
            "XML sitemap generation",
            "Robots.txt configuration",
            "Canonical URL management",
            "Schema markup implementation"
          ]
        },
        {
          title: "Off-Page SEO",
          icon: Globe,
          items: [
            "Backlink acquisition strategy",
            "Social signals and engagement",
            "Brand mentions and citations",
            "Guest posting opportunities",
            "Local business listings",
            "Influencer partnerships"
          ]
        }
      ]
    },
    aeo: {
      title: "AEO (Answer Engine Optimization)",
      subtitle: "Optimize for AI-powered search and voice assistants",
      sections: [
        {
          title: "Featured Snippets",
          icon: Target,
          items: [
            "Question-based content structure",
            "Concise answer formatting (40-60 words)",
            "Bullet points and numbered lists",
            "Definition-style responses",
            "Table and comparison formats",
            "Direct answer placement"
          ]
        },
        {
          title: "Structured Data",
          icon: BarChart3,
          items: [
            "FAQ schema implementation",
            "HowTo schema for tutorials",
            "Article schema for blog posts",
            "Product schema for e-commerce",
            "LocalBusiness schema",
            "Review and rating schema"
          ]
        },
        {
          title: "Voice Search",
          icon: Bot,
          items: [
            "Natural language optimization",
            "Long-tail conversational keywords",
            "Question-answer formatting",
            "Local intent optimization",
            "Speakable content markup",
            "Action-oriented responses"
          ]
        }
      ]
    },
    geo: {
      title: "GEO (Generative Engine Optimization)",
      subtitle: "Get cited by AI models like ChatGPT and Gemini",
      sections: [
        {
          title: "AI Citation Strategy",
          icon: Sparkles,
          items: [
            "Authoritative content creation",
            "Expert positioning and E-E-A-T",
            "Data-driven insights with sources",
            "Unique research and statistics",
            "Comprehensive topic coverage",
            "Regular content updates"
          ]
        },
        {
          title: "Content Structure",
          icon: BookOpen,
          items: [
            "Clear hierarchical organization",
            "Factual, verifiable information",
            "Contextual relevance signals",
            "Multi-format content (text, images, video)",
            "Cross-referencing and citations",
            "Semantic markup for AI understanding"
          ]
        },
        {
          title: "Brand Visibility",
          icon: TrendingUp,
          items: [
            "Consistent brand messaging",
            "Multiple platform presence",
            "User-generated content amplification",
            "Social proof and testimonials",
            "Industry thought leadership",
            "Partnership and collaboration signals"
          ]
        }
      ]
    },
    automation: {
      title: "AI-Powered SEO Automation",
      subtitle: "How AutomateSEO.ai handles everything for you",
      sections: [
        {
          title: "Content Generation",
          icon: FileText,
          items: [
            "AI-written blog posts (GPT-4o)",
            "Social media content calendar",
            "Product descriptions at scale",
            "Meta tags auto-generation",
            "FAQ content creation",
            "Email marketing copy"
          ]
        },
        {
          title: "Visual Content",
          icon: Sparkles,
          items: [
            "AI image generation (DALL-E 3)",
            "Social media graphics",
            "Blog featured images",
            "Product showcase visuals",
            "Infographic backgrounds",
            "Brand-consistent imagery"
          ]
        },
        {
          title: "Video Creation",
          icon: Play,
          items: [
            "AI video generation (Runway Gen-3)",
            "Avatar videos (HeyGen)",
            "Social media reels",
            "Product demos",
            "Explainer videos",
            "YouTube content"
          ]
        }
      ]
    }
  };

  const currentContent = seoContent[activeTab];

  return (
    <>
      <Helmet>
        <title>Complete SEO, AEO & GEO Guide 2025 | AutomateSEO.ai</title>
        <meta name="description" content="Learn how to automate SEO, AEO (Answer Engine Optimization), and GEO (Generative Engine Optimization) with AI. Comprehensive guide with actionable strategies." />
        <meta name="keywords" content="SEO automation, AEO, GEO, AI SEO, search engine optimization, answer engine optimization, generative engine optimization" />
        <link rel="canonical" href="https://automateseo.ai/seo-guide" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Complete SEO, AEO & GEO Guide 2025 | AutomateSEO.ai" />
        <meta property="og:description" content="Master SEO, AEO, and GEO automation with AI. Free comprehensive guide." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://automateseo.ai/seo-guide" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Navigation */}
        <nav className="fixed w-full z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-3">
                <img 
                  src="/AutomateSEO-logo.png" 
                  alt="AutomateSEO.ai" 
                  className="h-10 w-auto"
                />
                <span className="text-xl font-bold text-white">AutomateSEO.ai</span>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-300 hover:text-white">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-24 pb-12 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <BookOpen className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Complete Learning Resource</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Master <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">SEO, AEO & GEO</span>
              <br />with AI Automation
            </h1>
            
            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
              Everything you need to know about Search Engine Optimization, Answer Engine Optimization, 
              and Generative Engine Optimization — and how AutomateSEO.ai automates it all.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  Start Automating Now <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                <Play className="mr-2 w-5 h-5" /> Watch Demo
              </Button>
            </div>
          </div>
        </section>

        {/* Tab Navigation */}
        <section className="py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2 bg-slate-800/50 p-2 rounded-xl">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {currentContent.title}
              </h2>
              <p className="text-lg text-slate-400">
                {currentContent.subtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {currentContent.sections.map((section, index) => (
                <div 
                  key={index}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 hover:border-blue-500/50 transition-all"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{section.title}</h3>
                  </div>
                  
                  <ul className="space-y-3">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Automate Your SEO?
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Let AutomateSEO.ai handle all your SEO, AEO, and GEO optimization automatically.
                Start your free 3-day trial today.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  Start Free Trial <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-700/50 py-8 px-4">
          <div className="max-w-7xl mx-auto text-center text-slate-400">
            <p>© 2025 AutomateSEO.ai. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SEOGuidePage;
